import React, { useEffect, useReducer } from 'react';

import { Input } from '@material-ui/core';
import { DateRangeOutlined } from '@material-ui/icons';
import EventRoundedIcon from '@material-ui/icons/EventRounded';
import CustomMenu from 'components/CustomMenu';
import { useDatePicker } from 'components/hooks/useDatePicker';
import { StyledButton, StyledDivider, StyledTypography } from 'styles/common';

import { TEAM_DURATION_VALUES } from 'constants/ProjectConstants';
import { convertDateToString3 } from 'constants/helper';
import { StyledIcon } from 'constants/icons';

import { CustomOptionsContainer, CustomSectionContainer, DurationsContainer } from './DateFilterStyles';
import FilterDisplay from './FilterDisplay';
import { buildDateFilters, getSelectedFiltersCountFromMap } from './helper';

const COMPLETED_ON_FILTER_UPDATE = 'COMPLETED_ON_FILTER_UPDATE';
const COMPLETED_ON_FILTER_UPDATE_FROM = 'COMPLETED_ON_FILTER_UPDATE_FROM';
const COMPLETED_ON_FILTER_UPDATE_TO = 'COMPLETED_ON_FILTER_UPDATE_TO';
const COMPLETED_ON_FILTER_UPDATE_PREDEFINED = 'COMPLETED_ON_FILTER_UPDATE_PREDEFINED';

export const initialCompletedOnFilterState = {
  from: null,
  to: null,
  predefined: null,
};

export function buildCompletedOnFilterInitialState({ from, to }) {
  return { ...initialCompletedOnFilterState, from, to };
}

export function completedOnFilterReducer(state = initialCompletedOnFilterState, action) {
  switch (action.type) {
    case COMPLETED_ON_FILTER_UPDATE_FROM: {
      return { ...state, predefined: null, from: action.date };
    }
    case COMPLETED_ON_FILTER_UPDATE_TO: {
      return { ...state, predefined: null, to: action.date };
    }
    case COMPLETED_ON_FILTER_UPDATE_PREDEFINED: {
      return { ...initialCompletedOnFilterState, predefined: action.key };
    }
    case COMPLETED_ON_FILTER_UPDATE: {
      return { ...action.filters };
    }
    default:
      return state;
  }
}

export const buildCompletedOnFilters = buildDateFilters('COMPLETED_ON');

export default function CompletedOnFilter({ selectedFiltersDispatch, selectedFilters = {}, width = '20%' }) {
  const [state, currentFiltersDispatch] = useReducer(completedOnFilterReducer, initialCompletedOnFilterState);

  const onFromDateChange = (date) => {
    currentFiltersDispatch({ type: COMPLETED_ON_FILTER_UPDATE_FROM, date });
  };

  const onToDateChange = (date) => {
    currentFiltersDispatch({ type: COMPLETED_ON_FILTER_UPDATE_TO, date });
  };

  useEffect(() => {
    currentFiltersDispatch({ type: COMPLETED_ON_FILTER_UPDATE, filters: selectedFilters });
  }, [JSON.stringify(selectedFilters)]);

  const onFromDateClick = useDatePicker(onFromDateChange);
  const onToDateClick = useDatePicker(onToDateChange);

  function onApply() {
    selectedFiltersDispatch({ type: COMPLETED_ON_FILTER_UPDATE, filters: state });
    document.body.click();
  }

  function onPredefinedClick(key) {
    currentFiltersDispatch({ type: COMPLETED_ON_FILTER_UPDATE_PREDEFINED, key });
  }

  const selectedCount = getSelectedFiltersCountFromMap(selectedFilters);

  return (
    <CustomMenu
      icon={
        <FilterDisplay width={width} icon={<EventRoundedIcon />} name="Completed On" selectedCount={selectedCount} />
      }
    >
      <DurationsContainer>
        {TEAM_DURATION_VALUES.map((duration) => (
          <StyledButton
            variant="outlined"
            color={state.predefined === duration.value ? 'primary' : 'textSecondary'}
            onClick={onPredefinedClick.bind(null, duration.value)}
          >
            {duration.name.replace('Since ', '')}
          </StyledButton>
        ))}
      </DurationsContainer>
      <StyledDivider />
      <CustomSectionContainer>
        <StyledTypography bottomGutter={2} align="center">
          Select Custom Dates
        </StyledTypography>
        <CustomOptionsContainer>
          <Input
            readOnly
            value={state.from ? convertDateToString3(state.from) : null}
            startAdornment={
              <StyledIcon size="smallest" color="textSecondary" rightGutter={0.5}>
                <DateRangeOutlined />
              </StyledIcon>
            }
            placeholder={new Date().toDateString()}
            onClick={onFromDateClick}
          />
          <Input
            readOnly
            value={state.to ? convertDateToString3(state.to) : null}
            startAdornment={
              <StyledIcon size="smallest" color="textSecondary" rightGutter={0.5}>
                <DateRangeOutlined />
              </StyledIcon>
            }
            placeholder={new Date().toDateString()}
            onClick={onToDateClick}
          />
        </CustomOptionsContainer>
        <div>
          <StyledButton leftGutter={-2} topGutter={1} onClick={onApply} variant="text" color="primary">
            Apply
          </StyledButton>
        </div>
      </CustomSectionContainer>
    </CustomMenu>
  );
}
